import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, KeyRound, Loader2, Moon, Sun, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, downloadFile, formatErr, uploadFile } from "@/lib/api";
import { isAdmin } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useThemeMode } from "@/context/ThemeContext";
import { logoSrc, useBranding } from "@/hooks/useData";
import { Field, PageHeader } from "@/components/common";
import { ConfirmDialog } from "@/components/Dialogs";
import { cn } from "@/lib/utils";

const Card = ({ title, children, testid }) => (
  <section className="rounded-2xl border bg-card p-5" data-testid={testid}>
    <h3 className="mb-4 font-display font-bold">{title}</h3>
    {children}
  </section>
);

function Profile() {
  const { user, reload } = useAuth();
  const qc = useQueryClient();
  const [email, setEmail] = useState(user.email || "");
  const [color, setColor] = useState(user.color);
  const save = async () => {
    try {
      await api.patch("/users/me", { email, color });
      await reload();
      qc.invalidateQueries({ queryKey: ["users-lite"] });
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <Card title="My profile" testid="settings-profile">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Email (for password reset)"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email-input" /></Field>
        <Field label="Chat bubble colour"><input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} className="h-10 w-full cursor-pointer rounded-lg border bg-card" data-testid="profile-color-input" /></Field>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={save} data-testid="profile-save-button">Save profile</Button>
        <Button variant="outline" asChild><Link to="/change-password" data-testid="settings-change-password-link"><KeyRound className="mr-1.5 h-4 w-4" />Change password</Link></Button>
      </div>
    </Card>
  );
}

function Company() {
  const qc = useQueryClient();
  const { data: brand } = useBranding();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [name, setName] = useState("");
  const [tol, setTol] = useState(10);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (settings) {
      setName(settings.company_name || "");
      setTol(settings.budget_tolerance ?? 10);
    }
  }, [settings]);
  const put = async (body) => {
    await api.put("/settings", body);
    qc.invalidateQueries({ queryKey: ["settings"] });
    qc.invalidateQueries({ queryKey: ["branding"] });
  };
  const save = async () => {
    try {
      await put({ company_name: name, budget_tolerance: Number(tol) });
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const logo = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadFile(file, "logo");
      await put({ logo_path: r.path });
      toast.success("Logo updated");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Company" testid="settings-company">
      <div className="flex flex-col gap-5 sm:flex-row">
        <label className="flex h-28 w-28 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 overflow-hidden rounded-xl border-2 border-dashed text-xs text-muted-foreground hover:border-primary/50" data-testid="logo-upload">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : brand?.has_logo ? <img src={logoSrc(brand.logo_version)} alt="logo" className="h-full w-full object-contain p-2" /> : <><ImagePlus className="h-5 w-5" />Upload logo</>}
          <input type="file" accept="image/*" className="hidden" data-testid="logo-upload-input" onChange={(e) => logo(e.target.files?.[0])} />
        </label>
        <div className="grid flex-1 gap-4 sm:grid-cols-2">
          <Field label="Company name"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="company-name-input" /></Field>
          <Field label="Match budget tolerance (±%)" hint="Used by Matchmaking (Phase 3)"><Input type="number" min="0" max="50" value={tol} onChange={(e) => setTol(e.target.value)} data-testid="budget-tolerance-input" /></Field>
        </div>
      </div>
      <Button className="mt-4" onClick={save} data-testid="company-save-button">Save company settings</Button>
    </Card>
  );
}

function SampleData() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const done = !!settings?.sample_deleted;
  const run = async () => {
    try {
      const { data } = await api.delete("/admin/sample-data");
      toast.success(`Deleted ${data.leads} leads, ${data.properties} properties, ${data.users} sample users`);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(formatErr(e));
    }
    setOpen(false);
  };
  return (
    <Card title="Sample data" testid="settings-sample-data">
      <p className="text-sm text-muted-foreground">Removes everything marked SAMPLE (sample leads, properties and the 2 demo employees) before going live. Real users are never touched.</p>
      <Button variant="destructive" className="mt-4" disabled={done} onClick={() => setOpen(true)} data-testid="delete-sample-data-button"><Trash2 className="mr-1.5 h-4 w-4" />{done ? "Sample data already deleted" : "Delete all sample data"}</Button>
      <ConfirmDialog open={open} onOpenChange={setOpen} title="Delete all sample data?" text="This permanently removes all SAMPLE leads, properties and demo employees." onConfirm={run} testid="delete-sample-dialog" />
    </Card>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const { theme, setTheme } = useThemeMode();
  return (
    <>
      <PageHeader title="Settings" testid="settings-title" />
      <div className="space-y-6">
        <Card title="Appearance" testid="settings-appearance">
          <div className="flex gap-2">
            {[["light", Sun, "Light"], ["dark", Moon, "Dark"]].map(([k, I, l]) => (
              <button key={k} onClick={() => setTheme(k)} data-testid={`theme-${k}-option`} className={cn("flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition-colors sm:max-w-[160px]", theme === k ? "border-primary bg-primary/10 text-primary" : "hover:bg-secondary")}>
                <I className="h-4 w-4" />{l}
              </button>
            ))}
          </div>
        </Card>
        <Profile />
        {isAdmin(user) && <Company />}
        {isAdmin(user) && (
          <Card title="Backup" testid="settings-backup">
            <p className="text-sm text-muted-foreground">Download all data (users, leads, properties, tasks, chat, expenses, deals, plans, activity) as one Excel file.</p>
            <Button className="mt-4" variant="outline" onClick={() => downloadFile("/admin/backup", {}, "performed-backup.xlsx").catch((e) => toast.error(formatErr(e)))} data-testid="backup-export-button">Export all data</Button>
          </Card>
        )}
        {isAdmin(user) && <SampleData />}
      </div>
    </>
  );
}
